import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Download, QrCode, Save, ExternalLink, Plus, Trash2, GripVertical } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const APP_ORIGIN = process.env.REACT_APP_BACKEND_URL;

export default function FeedbackQrTab() {
  const { getHeaders } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [landing, setLanding] = useState(null);
  const [form, setForm] = useState({
    thank_you_title: '',
    thank_you_message: '',
    offer_text: '',
    offer_banner_b64: '',
    social_instagram: '',
    social_facebook: '',
    social_website: '',
    form_config: {
      food:      { enabled: true, label: 'Food', required: false },
      service:   { enabled: true, label: 'Service', required: false },
      ambience:  { enabled: true, label: 'Ambience', required: false },
      packaging: { enabled: true, label: 'Packaging', required: false },
      delivery:  { enabled: true, label: 'Delivery experience', required: false },
    },
    custom_questions: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/outlets`, { headers: getHeaders() })
      .then(res => {
        setOutlets(res.data);
        if (res.data?.length && !selectedOutlet) setSelectedOutlet(res.data[0].id);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLanding = useCallback(async () => {
    if (!selectedOutlet) return;
    const res = await axios.get(`${API_BASE}/admin/feedback/landing/${selectedOutlet}`, { headers: getHeaders() });
    setLanding(res.data);
    setForm({
      thank_you_title: res.data.thank_you_title || '',
      thank_you_message: res.data.thank_you_message || '',
      offer_text: res.data.offer_text || '',
      offer_banner_b64: res.data.offer_banner_b64 || '',
      social_instagram: res.data.social_instagram || '',
      social_facebook: res.data.social_facebook || '',
      social_website: res.data.social_website || '',
      form_config: res.data.form_config || {
        food: { enabled: true, label: 'Food', required: false },
        service: { enabled: true, label: 'Service', required: false },
        ambience: { enabled: true, label: 'Ambience', required: false },
        packaging: { enabled: true, label: 'Packaging', required: false },
        delivery: { enabled: true, label: 'Delivery experience', required: false },
      },
      custom_questions: Array.isArray(res.data.custom_questions) ? res.data.custom_questions : [],
    });
  }, [selectedOutlet, getHeaders]);

  useEffect(() => { loadLanding(); }, [loadLanding]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/admin/feedback/landing/${selectedOutlet}`, form, { headers: getHeaders() });
      toast.success('Landing page saved');
      loadLanding();
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error('Banner must be under 500KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, offer_banner_b64: reader.result }));
    reader.readAsDataURL(file);
  };

  const downloadQr = (formType) => {
    // Use a fetch → blob to avoid browser right-click menu on an <img>.
    fetch(`${API_BASE}/feedback/qr/${selectedOutlet}/${formType}`)
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const outlet = outlets.find(o => o.id === selectedOutlet);
        a.download = `${(outlet?.name || 'outlet').replace(/\s+/g, '_')}_${formType}_qr.png`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const feedbackUrl = type => `${APP_ORIGIN}/feedback/${selectedOutlet}/${type}`;

  if (!outlets.length) return <p className="text-sm text-[#5C6B64] py-4">No outlets available.</p>;

  return (
    <div className="space-y-4" data-testid="feedback-qr-tab">
      <Card className="border-[#E2E8DF]">
        <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
          <Label className="text-sm font-medium text-[#1A2421]">Outlet</Label>
          <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
            <SelectTrigger className="w-64 h-9" data-testid="qr-outlet-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* QR section */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <QrCode className="w-4 h-4 text-[color:var(--color-primary)]" /> QR codes
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[{ type: 'dinein', label: 'Dine-in feedback' }, { type: 'delivery', label: 'Delivery feedback' }].map(q => (
              <div key={q.type} className="border border-[#E2E8DF] rounded-lg p-3 text-center">
                <p className="text-xs font-semibold text-[#1A2421] uppercase tracking-wider mb-2">{q.label}</p>
                <div className="bg-[#F9F9F8] rounded-lg p-3 mb-3 flex items-center justify-center">
                  <img src={`${API_BASE}/feedback/qr/${selectedOutlet}/${q.type}`}
                    alt={`${q.label} QR`} className="w-44 h-44" data-testid={`qr-img-${q.type}`} />
                </div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <a href={feedbackUrl(q.type)} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-[#5C6B64] truncate flex items-center gap-1 hover:text-[color:var(--color-primary)]"
                    data-testid={`qr-url-${q.type}`}>
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    <span className="truncate">{feedbackUrl(q.type)}</span>
                  </a>
                </div>
                <Button onClick={() => downloadQr(q.type)} size="sm" variant="outline" className="w-full"
                  data-testid={`qr-download-${q.type}`}>
                  <Download className="w-3 h-3 mr-1" /> Download PNG
                </Button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#5C6B64] mt-3 text-center">
            Scan the QR → form opens directly. Print these and place at tables (dine-in) / include on delivery bills.
          </p>
        </CardContent>
      </Card>

      {/* Form fields editor */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-semibold text-[#1A2421]">Form fields</CardTitle>
          <p className="text-xs text-[#5C6B64]">Toggle rating categories on/off, rename labels, mark as required. Overall rating is always shown.</p>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="form-fields-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold border-b border-[#E2E8DF]">
                  <th className="px-2 py-2 text-left">Field</th>
                  <th className="px-2 py-2 text-left">Label shown to customer</th>
                  <th className="px-2 py-2 text-center">Enabled</th>
                  <th className="px-2 py-2 text-center">Required</th>
                  <th className="px-2 py-2 text-left">Applies to</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'food', default: 'Food', scope: 'Dine-in & Delivery' },
                  { key: 'service', default: 'Service', scope: 'Dine-in' },
                  { key: 'ambience', default: 'Ambience', scope: 'Dine-in' },
                  { key: 'packaging', default: 'Packaging', scope: 'Delivery' },
                  { key: 'delivery', default: 'Delivery experience', scope: 'Delivery' },
                ].map(field => {
                  const cfg = form.form_config[field.key] || { enabled: true, label: field.default, required: false };
                  const updateCfg = (patch) => setForm(f => ({
                    ...f,
                    form_config: { ...f.form_config, [field.key]: { ...cfg, ...patch } }
                  }));
                  return (
                    <tr key={field.key} className="border-b border-[#E2E8DF]/60" data-testid={`form-field-${field.key}`}>
                      <td className="px-2 py-2 font-medium text-[#1A2421] capitalize">{field.key}</td>
                      <td className="px-2 py-2">
                        <Input value={cfg.label || ''}
                          onChange={e => updateCfg({ label: e.target.value })}
                          placeholder={field.default}
                          data-testid={`field-label-${field.key}`}
                          className="h-8 text-xs" />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input type="checkbox" checked={cfg.enabled !== false}
                          onChange={e => updateCfg({ enabled: e.target.checked })}
                          data-testid={`field-enabled-${field.key}`}
                          className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer" />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input type="checkbox" checked={!!cfg.required}
                          onChange={e => updateCfg({ required: e.target.checked })}
                          data-testid={`field-required-${field.key}`}
                          className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer" />
                      </td>
                      <td className="px-2 py-2 text-[10px] text-[#5C6B64]">{field.scope}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Custom questions editor */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-semibold text-[#1A2421]">Custom questions</CardTitle>
          <p className="text-xs text-[#5C6B64]">
            Add your own questions — rating (1–5), yes/no, or short text. Shown in order below the standard ratings.
          </p>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2">
          {(form.custom_questions || []).length === 0 && (
            <p className="text-xs text-[#5C6B64] italic py-2" data-testid="custom-q-empty">
              No custom questions yet — add one below.
            </p>
          )}
          {(form.custom_questions || []).map((q, idx) => {
            const update = (patch) => setForm(f => ({
              ...f,
              custom_questions: f.custom_questions.map((x, i) => i === idx ? { ...x, ...patch } : x),
            }));
            const remove = () => setForm(f => ({
              ...f,
              custom_questions: f.custom_questions.filter((_, i) => i !== idx),
            }));
            const move = (dir) => setForm(f => {
              const arr = [...f.custom_questions];
              const j = idx + dir;
              if (j < 0 || j >= arr.length) return f;
              [arr[idx], arr[j]] = [arr[j], arr[idx]];
              return { ...f, custom_questions: arr.map((x, i) => ({ ...x, order: i })) };
            });
            return (
              <div key={q.id}
                className="flex items-start gap-2 p-2.5 rounded-lg border border-[#E2E8DF] bg-[#F9F9F8]"
                data-testid={`custom-q-row-${idx}`}>
                <div className="flex flex-col items-center gap-0.5 pt-1.5">
                  <button type="button" onClick={() => move(-1)} title="Move up"
                    className="text-[#5C6B64] hover:text-[color:var(--color-primary)] disabled:opacity-30"
                    disabled={idx === 0}
                    data-testid={`custom-q-up-${idx}`}>
                    <GripVertical className="w-3.5 h-3.5 rotate-90" />
                  </button>
                </div>
                <div className="flex-1 grid grid-cols-12 gap-2">
                  <Input value={q.question}
                    onChange={e => update({ question: e.target.value })}
                    placeholder="Your question…" maxLength={200}
                    data-testid={`custom-q-text-${idx}`}
                    className="col-span-12 sm:col-span-6 h-9 text-xs" />
                  <Select value={q.type} onValueChange={v => update({ type: v })}>
                    <SelectTrigger className="col-span-6 sm:col-span-3 h-9 text-xs" data-testid={`custom-q-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rating">Rating (1–5)</SelectItem>
                      <SelectItem value="yesno">Yes / No</SelectItem>
                      <SelectItem value="text">Short text</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="col-span-4 sm:col-span-2 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#1A2421] bg-white rounded h-9 px-2 border border-[#E2E8DF] cursor-pointer">
                    <input type="checkbox" checked={!!q.required}
                      onChange={e => update({ required: e.target.checked })}
                      data-testid={`custom-q-required-${idx}`}
                      className="w-3.5 h-3.5 accent-[var(--color-primary)] cursor-pointer" />
                    Required
                  </label>
                  <button type="button" onClick={remove}
                    data-testid={`custom-q-delete-${idx}`}
                    className="col-span-2 sm:col-span-1 h-9 rounded text-[#C45A41] hover:bg-[#FEF2EE] flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          <button type="button"
            data-testid="custom-q-add"
            onClick={() => setForm(f => ({
              ...f,
              custom_questions: [
                ...(f.custom_questions || []),
                {
                  id: `cq_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                  question: '',
                  type: 'rating',
                  required: false,
                  order: (f.custom_questions || []).length,
                },
              ],
            }))}
            className="w-full h-9 border border-dashed border-[color:var(--color-primary)]/40 text-[color:var(--color-primary)] rounded-lg text-xs font-semibold hover:bg-[color:var(--color-primary)]/5 flex items-center justify-center gap-1.5 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add question
          </button>
        </CardContent>
      </Card>

      {/* Landing page editor */}
      <Card className="border-[#E2E8DF] shadow-sm">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-semibold text-[#1A2421]">Post-submission landing page</CardTitle>
          <p className="text-xs text-[#5C6B64]">Customers see this after submitting. Use it as a marketing touchpoint.</p>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6B64] font-semibold">Thank-you title</Label>
              <Input value={form.thank_you_title}
                onChange={e => setForm(f => ({ ...f, thank_you_title: e.target.value }))}
                placeholder="Thank you!" data-testid="landing-title-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B64] font-semibold">Website URL</Label>
              <Input value={form.social_website}
                onChange={e => setForm(f => ({ ...f, social_website: e.target.value }))}
                placeholder="https://…" data-testid="landing-website-input" className="mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-xs text-[#5C6B64] font-semibold">Thank-you message</Label>
            <Textarea value={form.thank_you_message}
              onChange={e => setForm(f => ({ ...f, thank_you_message: e.target.value }))}
              rows={2} data-testid="landing-message-input" className="mt-1" />
          </div>

          <div>
            <Label className="text-xs text-[#5C6B64] font-semibold">Special offer (plain text)</Label>
            <Textarea value={form.offer_text}
              onChange={e => setForm(f => ({ ...f, offer_text: e.target.value }))}
              rows={2} placeholder="e.g. 10% off your next visit — use code THANK10"
              data-testid="landing-offer-input" className="mt-1" />
          </div>

          <div>
            <Label className="text-xs text-[#5C6B64] font-semibold">Offer banner image (optional, ≤500KB)</Label>
            <div className="flex items-center gap-3 mt-1">
              <input type="file" accept="image/*" onChange={handleBannerUpload}
                className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[color:var(--color-primary)] file:text-white file:text-[11px] file:cursor-pointer"
                data-testid="landing-banner-upload" />
              {form.offer_banner_b64 && (
                <>
                  <img src={form.offer_banner_b64} alt="preview" className="h-12 w-auto rounded border border-[#E2E8DF]" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, offer_banner_b64: '' }))}
                    className="text-[10px] text-[#C45A41] underline">Remove</button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6B64] font-semibold">Instagram URL</Label>
              <Input value={form.social_instagram}
                onChange={e => setForm(f => ({ ...f, social_instagram: e.target.value }))}
                placeholder="https://instagram.com/…" data-testid="landing-instagram-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#5C6B64] font-semibold">Facebook URL</Label>
              <Input value={form.social_facebook}
                onChange={e => setForm(f => ({ ...f, social_facebook: e.target.value }))}
                placeholder="https://facebook.com/…" data-testid="landing-facebook-input" className="mt-1" />
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <a href={`/feedback/${selectedOutlet}/thanks`} target="_blank" rel="noopener noreferrer"
              className="text-xs text-[color:var(--color-primary)] underline flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Preview landing page
            </a>
            <Button onClick={handleSave} disabled={saving} data-testid="save-landing-btn"
              className="bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save landing page'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
